/**
 * WizardProgress — the "‹ Back" affordance + step dots from the mock (`.wiz__back` / `.wiz__dots`).
 * Two done = sage, the active step = coral and wider, the rest neutral. The dot rail is decorative
 * (`aria-hidden`); the accessible step position rides on the visually-hidden status line so a screen
 * reader hears "Step 3 of 5" without the pill row being announced dot by dot.
 */
import { cn } from '@/lib/utils';

interface WizardProgressProps {
  /** 0-based index of the active step. */
  step: number;
  total: number;
  /** Back handler; when omitted (first step) the back control is a non-interactive placeholder. */
  onBack?: () => void;
}

export function WizardProgress({ step, total, onBack }: WizardProgressProps) {
  return (
    <div className="flex items-center justify-between py-1.5">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="-ml-1 rounded-md px-1 py-1 text-[13px] font-medium text-ink-quiet transition-colors duration-ui ease-thread hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25"
        >
          ‹ Back
        </button>
      ) : (
        <span className="text-[13px] font-medium text-ink-38">‹ Back</span>
      )}

      <p className="sr-only" role="status">
        Step {step + 1} of {total}
      </p>

      <div className="flex gap-1.5" aria-hidden="true">
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={cn(
              'block h-[5px] rounded-full transition-all duration-ui ease-thread',
              i === step ? 'w-[26px] bg-coral' : i < step ? 'w-[18px] bg-sage' : 'w-[18px] bg-line-2',
            )}
          />
        ))}
      </div>
    </div>
  );
}
